import { useCurrentWorkspace } from '@gripp/shared-logic';
import { IntegrationDigestRow, IntegrationSyncDigestCard } from '@gripp/shared-ui';
import { Pressable, StyleSheet, View } from 'react-native';
import { useNavigate } from 'react-router-dom';

type IntegrationDigestSectionProps = {
  rows: IntegrationDigestRow[];
  /** digestKey of the currently-open detail panel, for selected-state styling. */
  selectedDigestKey?: string;
};

/**
 * Renders the integration sync digest section at the top of the activity feed
 * (above the campaign section). Each digest renders as a standard activity-list
 * row (`IntegrationSyncDigestCard`) — same idiom as the sibling issue/campaign
 * rows. Tapping a row opens a right-hand SlideLayout detail panel showing the
 * per-asset breakdown (see `IntegrationDigestPanel` + the
 * `activities/digest/:digestKey` route).
 *
 * Navigation rules (per the GeoTab spec revision, GRIPP #2887):
 * - Tapping the card navigates to the digest detail panel (it does NOT auto-
 *   select an issue — digest rows are excluded from first-row auto-select in
 *   `activities.tsx`, and a digest is an aggregate, not a single conversation).
 * - The per-asset breakdown — and asset-detail navigation from it — lives in
 *   the detail panel, not an expand-in-place list.
 */
export const IntegrationDigestSection = ({
  rows,
  selectedDigestKey,
}: IntegrationDigestSectionProps) => {
  const navigate = useNavigate();
  // The feed digest is workspace-scoped, so the pill shows the workspace name —
  // the same `asset.name || workspace.name` slot sibling rows use.
  const workspace = useCurrentWorkspace();

  return (
    <View style={styles.section}>
      {rows.map((row) => (
        <Pressable
          key={row.digestKey}
          testID={`integration-digest-row-${row.digestKey}`}
          accessibilityRole="button"
          accessibilityLabel={`Open sync digest for ${row.provider} on ${row.day}`}
          onPress={() =>
            navigate(
              `/activities/digest/${encodeURIComponent(row.digestKey)}`
            )
          }
        >
          <IntegrationSyncDigestCard
            digest={row}
            workspaceName={workspace?.name}
            isSelected={selectedDigestKey === row.digestKey}
          />
        </Pressable>
      ))}
    </View>
  );
};

const styles = StyleSheet.create({
  section: {
    // Row separators are drawn by `ActivityCard` itself; the wrapper is layout
    // only and inherits the column's brand-white background.
  },
});
